/**
 * Sheet 08, the subscribe page (mission 9 WP12 T073, T075; FR-013, FR-019, FR-023, NFR-011, NFR-012;
 * C-002, C-003, C-007, C-008, C-012; B.1, B.2, B.5, B.7, T.5, NF.9; spec amendment 10; OR-10, OR-13,
 * OR-20; squad U23, U24, U25, S22; analysis run 1 A3, run 2 G3; contracts/ui-api.md "Sheet 08").
 *
 * `/app/subscribe` is a pending tenant's only app page (AuthGate, OR-10). By the `checkout` parameter:
 * `complete` is the checkout's return (`CheckoutReturn`), `cancelled` states `CheckoutCancelled` above
 * the ready page, and anything else is the page itself.
 *
 * The page makes one billing read (`billingQuery`; NFR-012) and reads the tenant AuthGate already holds.
 * In the drawn order (and the keyboard order toggle, email, acknowledgment, Subscribe):
 * - the onboarding stepper (08/1) and the title;
 * - `PlanToggle` (08/3) and `PlanTerms` (08/10, 08/11), every figure from the billing read (C-010);
 * - the soft-cap statement (08/7, B.5) with the forgiveness ceiling, for a tenant that has never had a plan
 *   only: a plan that ended already had its first cycle, and a resubscribed tenant gets no second soft cap
 *   (FR-003, FR-009), so the page does not promise one;
 * - `MeteringStatement` (OR-13) and `DisclaimerBand` with `T5_DISCLOSURE` verbatim, never dismissible,
 *   above the action (08/8; C-003);
 * - the billing email (08/5, B.7, NF.9), defaulting to the stored billing email, else the login email
 *   (`contact_email`): a pending tenant has none stored, so it gets the login email (FR-013), and a
 *   cancelled tenant resubscribing keeps the address it chose (review cycle 1). Required: when both are
 *   empty the field is empty and nothing is sent until one is entered;
 * - `CancelTermsAck` (08/6), then Subscribe (08/9), disabled with `subscribeDisabledReason` until it is
 *   ticked, and only then (amendment 10). There is no card field: card entry is on the provider's hosted
 *   page (08/4; NFR §1.3, OR-20), which `cardProviderNote` states.
 * Subscribe starts the checkout (`plan`, `terms.cancel_terms_version`, the billing email) and leaves for
 * the provider inside the mutation (`leaveForProvider`), so the URL never reaches state or the DOM (S22);
 * it stays busy while the browser leaves. A refused URL is `providerUnavailable` (`unsafe-url`), a 503 is
 * `genericError` (`provider-unavailable`), and a refusal renders its register row, never the server's
 * detail (U25), all through `BillingProblem`.
 *
 * A derived `read_only` tenant (the billing read's `access`, the same derivation as the credits read's
 * `billing` member) stays on this page. Reason `cancelled` sees the ready page and may subscribe again
 * (`readonly-cancelled`). Reason `dunning` is answered by the 409 `dunning` row with the update-payment
 * link to `/app/settings/billing#card` in place of the ready page, since the server refuses that checkout
 * (`readonly-dunning`; analysis run 1, A3). The trial (08/12) renders nothing (C-008), and one identity per
 * tenant means no "Ask an Owner" (08/2; C-007).
 */
import { useQuery } from '@tanstack/react-query'
import { useId, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { DATA_PATH, useTenant } from '@/components/trellis/AuthGate'
import { DisclaimerBand } from '@/components/trellis/DisclaimerBand'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { Problem } from '@/lib/api'
import type { BillingView, PlanId } from '@/lib/api/billing'
import { T5_DISCLOSURE } from '@/lib/mandatory-copy'
import { PROBLEM } from '@/lib/problems'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { billingQuery, useCheckoutMutation } from '@/lib/queries'
import { BillingProblem } from '../settings/billing/BillingProblem'
import { CancelTermsAck } from './CancelTermsAck'
import { CheckoutCancelled, CheckoutReturn } from './CheckoutReturn'
import { MeteringStatement } from './MeteringStatement'
import { OnboardingStepper } from './OnboardingStepper'
import { PlanTerms } from './PlanTerms'
import { PlanToggle } from './PlanToggle'

const LINK = 'text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]'

/** The checkout's 409 for a failed payment, as the server answers it (billing-api refusal 6; A3). */
const DUNNING_CONFLICT: Problem = {
  type: PROBLEM.BILLING_STATE_CONFLICT,
  title: copyText('problemBillingConflictTitle'),
  status: 409,
  requirement: 'B.1',
  reason: 'dunning',
  remedy: 'update-payment',
}

function SubscribeForm({ view, defaultEmail, titleId }: { view: BillingView; defaultEmail: string; titleId: string }) {
  const checkout = useCheckoutMutation()
  const emailId = useId()
  const hintId = useId()
  const [plan, setPlan] = useState<PlanId>('monthly')
  const [ticked, setTicked] = useState(false)
  const [edited, setEdited] = useState<string | null>(null)
  const billingEmail = edited ?? defaultEmail
  // Busy from the click until the browser has left for the provider.
  const busy = checkout.isPending || checkout.isSuccess
  const { terms } = view

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!ticked || busy || !event.currentTarget.reportValidity()) return
    checkout.mutate({ plan, cancel_terms_version: terms.cancel_terms_version, billing_email: billingEmail.trim() })
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-6">
      <section className="flex flex-col gap-4">
        <PlanToggle prices={view.prices} allowance={terms.allowance_per_cycle} plan={plan} onPlanChange={setPlan} labelledBy={titleId} />
        <PlanTerms terms={terms} tiers={view.tiers} />
        {view.access.state === 'pending_subscription' ? (
          <p className="text-body-sm text-navy-700">{PROVISIONAL_COPY.softCapStatement.render({ ceiling: terms.forgiveness_ceiling })}</p>
        ) : null}
      </section>
      <MeteringStatement />
      <DisclaimerBand>{T5_DISCLOSURE}</DisclaimerBand>
      <div className="flex max-w-[440px] flex-col gap-1.5">
        <Label htmlFor={emailId}>{copyText('billingEmailLabel')}</Label>
        <Input
          id={emailId}
          type="email"
          autoComplete="email"
          required
          aria-describedby={hintId}
          value={billingEmail}
          onChange={(event) => setEdited(event.target.value)}
        />
        <p id={hintId} className="text-caption text-navy-600">
          {copyText('billingEmailHint')}
        </p>
      </div>
      <CancelTermsAck annual={plan === 'annual'} checked={ticked} onCheckedChange={setTicked} />
      {checkout.error === null ? null : <BillingProblem error={checkout.error} />}
      <div className="flex flex-col items-start gap-2">
        {ticked ? (
          <Button type="submit" loading={busy}>
            {copyText('subscribeAction')}
          </Button>
        ) : (
          <Button type="submit" disabled disabledReason={copyText('subscribeDisabledReason')}>
            {copyText('subscribeAction')}
          </Button>
        )}
        <p className="text-caption text-navy-600">{copyText('cardProviderNote')}</p>
      </div>
    </form>
  )
}

function SubscribePage({ cancelled }: { cancelled: boolean }) {
  const billing = useQuery(billingQuery())
  const tenant = useTenant()
  const titleId = useId()
  const view = billing.data

  let body
  if (view === undefined) {
    body = billing.isError ? (
      <BillingProblem error={billing.error} />
    ) : (
      <p className="text-body-sm text-navy-600">{copyText('loading')}</p>
    )
  } else if (view.access.state === 'read_only' && view.access.reason === 'dunning') {
    body = <ProblemBand problem={DUNNING_CONFLICT} />
  } else {
    body = <SubscribeForm view={view} defaultEmail={view.billing_email ?? tenant.data?.contact_email ?? ''} titleId={titleId} />
  }

  return (
    <div className="flex max-w-[880px] flex-col gap-6">
      <OnboardingStepper />
      <h1 id={titleId} className="text-h1 text-navy-950">
        {copyText('subscribeTitle')}
      </h1>
      {cancelled ? <CheckoutCancelled /> : null}
      {body}
      <p className="text-caption text-navy-600">
        {copyText('subscribeDeleteNote')}{' '}
        <Link to={DATA_PATH} className={LINK}>
          {copyText('subscribeDeleteLink')}
        </Link>
      </p>
    </div>
  )
}

export default function Subscribe() {
  const [params] = useSearchParams()
  const checkout = params.get('checkout')
  if (checkout === 'complete') return <CheckoutReturn />
  return <SubscribePage cancelled={checkout === 'cancelled'} />
}
